import type { IAnnotation } from "@bronte/viewer/src/main";

export type StageStyles = {
  stage?: {
    maxWidth?: number;
    background?: string;
    responsive?: boolean;
    padding?: {
      vertical?: number;
      horizontal?: number;
    };
    border?: {
      color?: string;
      radius?: number;
      width?: number;
    };
  };
  elements?: {
    padding?: {
      vertical?: number;
      horizontal?: number;
    };
  };
};

export type ScreenConfig = {
  styles?: StageStyles;
  stage: {
    text?: string;
    ref?: string;
    path?: string;
    sections?: Array<{
      rowsAreFullWidth?: boolean;
      backgroundImageUrl?: string;
      cssClass?: string;
      ref?: string;
      backgroundImageAltText?: string;
      rows?: Array<{
        ref?: string;
        columns?: Array<{
          ref?: string;
          stageElements?: StageElement[];
        }>;
      }>;
    }>;
  };
  screenType?: string;
  title?: string;
  components?: Component[];
};

export interface StageElement {
  type?: string;
  ref?: string;
  config?: Record<string, unknown>;
  localRef?: string;
  height?: number | string;
  style?: {
    height?: number | string;
    [key: string]: any;
  };
  layout?: {
    height?: number;
    [key: string]: any;
  };
}

export interface Component {
  localRef?: string;
  $ambrosia?: string;
  $id?: string;
  configSchemaVersion?: string;
  config?: Record<string, unknown>;
  annotations?: unknown[];
  pluginPayload?: {
    pluginRepositoryPath?: string;
    summary?: {
      pluginId?: string;
      name?: string;
      description?: string;
      type?: string;
      latestVersion?: string;
      subscriptionId?: string;
      createdAt?: string;
      updatedAt?: string;
      tags?: string[];
      publishMode?: string;
    };
  };
  assets?: unknown[];
}

// Image config type matching SPF ImageElement structure
export type ImageElementConfig = {
  size?: {
    width?: number;
  };
  url?:
    | string
    | {
        source?: {
          original?: { url: string; width?: number; height?: number };
          compressed?: { url: string; width?: number; height?: number };
          large?: { url: string; width?: number; height?: number };
          medium?: { url: string; width?: number; height?: number };
          small?: { url: string; width?: number; height?: number };
        };
        metadata?: {
          altText?: string;
          longDesc?: string;
        };
      };
  alt?: string;
  caption?: string;
  credit?: string;
  activityLabel?: string;
  activityTitle?: string;
  layout?: {
    captionPosition?: string;
    alignment?: string;
  };
  cssClass?: string;
};

/** Spotlight highlight for TTS: target element by data-id and mark character ranges with background color */
export interface Spotlight {
  selector: string;
  mark: Array<{
    startOffset: number;
    endOffset?: number;
    length: number;
    text?: string;
    backgroundColor: string;
    isUnderLineText?: boolean;
    underLineColor?: string;
  }>;
  useTextSearch?: boolean;
  text?: string;
}

export type SpotlightApplyOptions = Spotlight | Spotlight[];

export interface HighlightSettings {
  icons?: Array<{
    type: string;
    image: string;
  }>;
  colors?: Array<{
    id: string;
    hex: string;
    pattern: string;
  }>;
}

export interface ThemeOptions {
  enableShowHide: boolean;
  fontSelector: string;
  fontSize: number;
  isAnnotationHide: boolean;
  isFitToPage: boolean;
  isFitToWidth: boolean;
  isHotspotsHide: boolean;
  lineSpacing: string;
  sliderVal: number;
  textTransform: string;
  variantId: string;
  continuousScroll: boolean;
  highlight?: HighlightSettings;
}

export type ThemeVariant = {
  themeId: string;
  variantId: string;
  variantName: string;
  config: string;
  state?: string;
  css?: string;
  backgroundColor?: string;
  surfaceColor?: string;
  textColor?: string;
  /** Unscoped CSS for body-portaled popovers (reader settings + theme colors). */
  popoverCss?: string;
};

export type ThemeVariants = Array<ThemeVariant>;

export interface AnnotationsAPI {
  load: (annotations: IAnnotation[], options?: { hideAnnotations?: boolean }) => void;
  add: (annotation: IAnnotation) => void;
  edit: (annotation: IAnnotation) => void;
  delete: (annotation: IAnnotation) => void;
  show: (showAnnotations: boolean) => void;
  clearSelection: () => void;
}

export interface MediaAPI {
  play: () => void;
  pause: () => void;
}

export interface SpotlightAPI {
  apply: (options: SpotlightApplyOptions) => void;
  clear: () => void;
}

export interface SearchAPI {
  clear: () => void;
}

export type Font = "Sassoon Sans";

export type GetAuthTokenCallback = (callback: (token: string) => void) => void;

import type { DeviceType } from "../constants";
import { DEVICE_TYPE } from "../constants";

/** Host-supplied device context threaded into plugin renderers. */
export type ViewerDeviceRuntime = {
  deviceType: DeviceType;
  isOnline: () => boolean;
  packagePath?: string;
};

/**
 * Fallback device runtime for standalone usage (dev shell, storybook).
 * In a full viewer host, `viewerDevice` is always provided.
 */
export function getDefaultViewerDeviceRuntime(): ViewerDeviceRuntime {
  return {
    deviceType: DEVICE_TYPE.WEB,
    isOnline: () => true,
  };
}
